import { ApiError } from "@/api/client";
import type { PublishRequirement } from "@/domain/selling";
import { authErrorMessage, errorField } from "@/pages/auth/auth-errors";

const MESSAGES: Record<string, string> = {
  slug_taken: "That store URL is taken.",
  slug_reserved: "That store URL is reserved.",
  slug_invalid: "Use 3–40 letters, numbers, - or _, starting and ending with a letter or number.",
  slug_locked: "A live store's URL can't change.",
  draft_exists: "You already have a store in progress. Finish setting that one up first.",
  store_limit: "You've opened as many stores as you can today. Try again tomorrow.",
  terms_outdated: "The seller terms have changed. Review them again to continue.",
};

/**
 * Where a selling error surfaced: publishing a draft, or editing a store's details (a live store must stay
 * complete, so an edit can be refused for the same missing fields a publish would be).
 */
export type SellErrorContext = "publish" | "edit";

const REQUIREMENTS: Record<PublishRequirement, string> = {
  name: "a name",
  tagline: "a tagline",
  categories: "at least one category",
};

function listRequirements(missing: PublishRequirement[]): string {
  const parts = missing.map((requirement) => REQUIREMENTS[requirement]);
  return parts.length <= 1 ? (parts[0] ?? "") : `${parts.slice(0, -1).join(", ")} and ${parts.at(-1)}`;
}

/** The `missing` list a `store_incomplete` response carries, keeping only requirements this client knows. */
function missingRequirements(error: ApiError): PublishRequirement[] {
  const missing = (error.body as { missing?: unknown } | undefined)?.missing;
  if (!Array.isArray(missing)) return [];
  return (Object.keys(REQUIREMENTS) as PublishRequirement[]).filter((requirement) => missing.includes(requirement));
}

function incompleteMessage(error: ApiError, context: SellErrorContext): string {
  const missing = listRequirements(missingRequirements(error));
  if (context === "edit") {
    return missing ? `A live store needs ${missing}.` : "A live store needs a name, a tagline and at least one category.";
  }
  return missing ? `Add ${missing} before publishing.` : "Fill in the store's details before publishing.";
}

/** User-facing copy for selling errors: known codes first, then the server's own 4xx message. */
export function sellErrorMessage(error: unknown, context: SellErrorContext = "publish"): string {
  if (error instanceof ApiError && error.status !== 429 && error.status < 500) {
    if (error.code === "store_incomplete") return incompleteMessage(error, context);
    if (error.code && MESSAGES[error.code]) return MESSAGES[error.code]!;
    if (error.message && error.code !== "forbidden") return error.message;
    if (error.code === "forbidden" && error.message && !error.message.startsWith("Cross-site")) return error.message;
  }
  return authErrorMessage(error);
}

export { errorField };
